/**
 * Sending a registered drop, shared by the Upload page and every "Add files" (Dax, 2026-10-05): photos made
 * into the JPEG we keep, then the files sent a page of upload sessions at a time (the next page opening while
 * this one sends), each completed as it lands so the server can queue what is ready.
 */
import { AUDIO_MIME_BY_EXT, IMAGE_MIME_BY_EXT, UPLOAD_SESSION_PAGE, VIDEO_MIME_BY_EXT, uploadKindOf, type UploadEntry, type UploadFile, type UploadSession } from '@studio/contracts';
import type { DroppedFile } from './drop-files';
import { toStillJpeg } from './still-convert';
import { trpcClient } from './trpc';
import { putResumable } from './upload';

const ALL_MIME_BY_EXT: Record<string, string> = { ...VIDEO_MIME_BY_EXT, ...IMAGE_MIME_BY_EXT, ...AUDIO_MIME_BY_EXT };

/** Photos are kept as JPEG: each one this browser can decode becomes it here; one it cannot goes as it is. */
export async function preparePhotos(dropped: DroppedFile[]): Promise<DroppedFile[]> {
  const out: DroppedFile[] = [];
  for (const f of dropped) out.push(uploadKindOf(f.file.name) === 'image' ? { ...f, file: (await toStillJpeg(f.file)) ?? f.file } : f);
  return out;
}

/** A file as the server registers it: a converted photo travels as image/jpeg under its own name. */
export function uploadSpec(f: DroppedFile): UploadFile {
  const ext = f.file.name.slice(f.file.name.lastIndexOf('.') + 1).toLowerCase();
  const converted = f.file.type === 'image/jpeg' && uploadKindOf(f.file.name) === 'image';
  return { name: f.file.name, bytes: f.file.size, contentType: converted ? 'image/jpeg' : ALL_MIME_BY_EXT[ext]!, path: f.path };
}

export interface SendHooks {
  signal?: AbortSignal;
  onProgress?: (entry: UploadEntry, sentBytes: number) => void;
  onLanded?: (entry: UploadEntry, file: File) => void | Promise<void>;
  onFailed?: (entry: UploadEntry) => void;
}

/** One file's own key while it sends: its part (several parts can share a clip), else its sound's row. */
export function entryKey(entry: UploadEntry): string {
  return entry.partId ?? entry.assetId;
}

/** Send every registered file of a drop; one that fails is reported and the rest carry on. */
export async function sendEntries(sending: { entry: UploadEntry; file: File }[], hooks: SendHooks = {}): Promise<void> {
  const pages: (typeof sending)[] = [];
  for (let i = 0; i < sending.length; i += UPLOAD_SESSION_PAGE) pages.push(sending.slice(i, i + UPLOAD_SESSION_PAGE));
  // Settled either way, so a page opened ahead never rejects unwatched.
  const open = (page: typeof sending): Promise<UploadSession[] | Error> =>
    trpcClient.upload.sessions.mutate({ entries: page.map((x) => x.entry) })
      .then((r) => r, (err: unknown) => (err instanceof Error ? err : new Error(String(err))));
  let ahead = pages.length ? open(pages[0]!) : null;
  for (const [p, page] of pages.entries()) {
    const sessions = await ahead!;
    ahead = pages[p + 1] ? open(pages[p + 1]!) : null;
    if (hooks.signal?.aborted) return;
    if (sessions instanceof Error) {
      for (const x of page) hooks.onFailed?.(x.entry);
      continue;
    }
    for (const [j, session] of sessions.entries()) {
      if (hooks.signal?.aborted) return;
      const file = page[j]!.file;
      try {
        if (session.sessionUri) await putResumable(session.sessionUri, file, (sent) => hooks.onProgress?.(session, sent), hooks.signal);
        await trpcClient.upload.complete.mutate({ assetId: session.assetId, partId: session.partId });
        hooks.onProgress?.(session, file.size);
        await hooks.onLanded?.(session, file);
      } catch {
        if (hooks.signal?.aborted) return;
        hooks.onFailed?.(session);
      }
    }
  }
}
